import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Phone, ChevronRight, CheckCircle2, XCircle, Clock, Trophy, AlertCircle, Sparkles } from 'lucide-react';
import { LOGO, WA_NUMBER, PHONE, API } from '@/pages/LandingPage';

export default function DailyMockTest() {
  const [stage, setStage] = useState('intro'); // intro | quiz | result
  const [quiz, setQuiz] = useState(null);
  const [guest, setGuest] = useState({ name: '', phone: '' });
  const [answers, setAnswers] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Free Daily PSC Mock Test | Signature PSC Coaching, Varkala';
    let meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Take a free daily Kerala PSC mock test online. Instant results. By Signature PSC Coaching Centre, Varkala.');
    axios.get(`${API}/quizzes/today`).then(r => {
      if (r.data) setQuiz(r.data);
      setAnswers(new Array(r.data?.total_questions || 0).fill(null));
    }).catch(() => {});
  }, []);

  const startQuiz = (e) => {
    e.preventDefault();
    if (!guest.name.trim() || !guest.phone.trim()) {
      toast.error('Name and WhatsApp number required'); return;
    }
    if (!/^\d{10}$/.test(guest.phone.replace(/\D/g, '').slice(-10))) {
      toast.error('Enter valid 10-digit WhatsApp number'); return;
    }
    setStage('quiz');
    setCurrentQ(0);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quiz-attempts/submit`, {
        quiz_id: quiz.id, answers, guest_name: guest.name, guest_phone: guest.phone
      });
      setResult(res.data);
      setStage('result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    }
    setLoading(false);
  };

  const setAnswer = (qIdx, optIdx) => {
    const next = [...answers]; next[qIdx] = optIdx; setAnswers(next);
  };

  if (!quiz) {
    return (
      <div className="min-h-screen bg-[#0c1a0e] flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-white/40 text-lg mb-4">Loading today's mock test...</div>
          <p className="text-white/60 text-sm">If this takes too long, no quiz has been published yet.</p>
          <Link to="/" className="inline-block mt-6 text-[#c8a84e]">Back to Home →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0c1a0e]" data-testid="daily-mock-test-page">
      <nav className="bg-[#0c1a0e]/95 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="" className="w-9 h-9 rounded-full bg-white p-0.5" />
            <div>
              <span className="text-sm font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>SIGNATURE PSC</span>
              <span className="text-[10px] block text-[#c8b87a] tracking-[0.15em] uppercase -mt-0.5">Free Daily Mock Test</span>
            </div>
          </Link>
          <a href={`tel:+91${PHONE}`} className="hidden sm:flex items-center gap-1.5 text-sm text-[#c8b87a]"><Phone className="w-4 h-4" /> {PHONE}</a>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {stage === 'intro' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="text-center">
              <Sparkles className="w-10 h-10 text-[#c8a84e] mx-auto mb-3" />
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Free Daily PSC Mock Test</h1>
              <p className="text-white/60 text-sm max-w-xl mx-auto">{quiz.title}</p>
            </div>
            <div className="p-6 rounded-2xl bg-[#142318] border border-white/10">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5 text-center">
                <div><p className="text-2xl font-bold text-[#c8a84e]">{quiz.total_questions}</p><p className="text-xs text-white/50">Questions</p></div>
                <div><p className="text-2xl font-bold text-emerald-400">+1</p><p className="text-xs text-white/50">Correct</p></div>
                <div><p className="text-2xl font-bold text-red-400">-1/3</p><p className="text-xs text-white/50">Wrong</p></div>
                <div><p className="text-2xl font-bold text-white/70">0</p><p className="text-xs text-white/50">Skipped</p></div>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-5 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-200/80">Negative marking applies. If you don't know an answer, skipping is better than guessing.</p>
              </div>
              <form onSubmit={startQuiz} className="space-y-4">
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-wider mb-1.5 block">Your Name *</Label>
                  <Input value={guest.name} onChange={e => setGuest({ ...guest, name: e.target.value })} placeholder="Full name" required
                    className="bg-[#0c1a0e] border-white/10 text-white" data-testid="guest-name" />
                </div>
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-wider mb-1.5 block">WhatsApp Number *</Label>
                  <Input value={guest.phone} onChange={e => setGuest({ ...guest, phone: e.target.value })} placeholder="10-digit mobile" required
                    className="bg-[#0c1a0e] border-white/10 text-white" data-testid="guest-phone" />
                  <p className="text-[11px] text-white/40 mt-1">We'll send your results & future mock tests on WhatsApp</p>
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white font-semibold py-3 rounded-full" data-testid="start-quiz-btn">
                  Start Mock Test <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </form>
            </div>
            <div className="text-center text-xs text-white/40">
              Already a student? <Link to="/login" className="text-[#c8a84e]">Login</Link> to track your scores
            </div>
          </motion.div>
        )}

        {stage === 'quiz' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 sticky top-16 bg-[#0c1a0e]/95 backdrop-blur py-3 z-30">
              <div className="text-sm text-white/70">Question <span className="text-[#c8a84e] font-bold">{currentQ + 1}</span> of {quiz.total_questions}</div>
              <div className="flex gap-1 text-xs">
                <span className="text-emerald-400">{answers.filter(a => a !== null).length} answered</span>
                <span className="text-white/40"> · </span>
                <span className="text-white/50">{answers.filter(a => a === null).length} left</span>
              </div>
            </div>
            <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] transition-all" style={{ width: `${((currentQ + 1) / quiz.total_questions) * 100}%` }} />
            </div>
            <motion.div key={currentQ} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="p-6 rounded-2xl bg-[#142318] border border-white/10" data-testid="quiz-question">
              <p className="text-base sm:text-lg text-white font-semibold mb-5 leading-snug whitespace-pre-wrap">{quiz.questions[currentQ].question}</p>
              <div className="space-y-2">
                {quiz.questions[currentQ].options.map((opt, i) => (
                  <button key={i} onClick={() => setAnswer(currentQ, i)}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-all flex items-center gap-3 ${
                      answers[currentQ] === i
                        ? 'border-[#c8a84e] bg-[#c8a84e]/10 text-white'
                        : 'border-white/10 text-white/70 hover:border-white/30'
                    }`} data-testid={`option-${i}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      answers[currentQ] === i ? 'bg-[#c8a84e] text-[#0c1a0e]' : 'bg-white/10 text-white/60'
                    }`}>{String.fromCharCode(65 + i)}</span>
                    <span className="text-sm">{opt}</span>
                  </button>
                ))}
              </div>
              <div className="flex justify-between mt-6 gap-3">
                <Button variant="outline" onClick={() => setAnswer(currentQ, null)} className="border-white/15 text-white/60 hover:bg-white/5 bg-transparent">Skip</Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0} className="border-white/15 text-white hover:bg-white/5 bg-transparent">← Prev</Button>
                  {currentQ < quiz.total_questions - 1 ? (
                    <Button onClick={() => setCurrentQ(currentQ + 1)} className="bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white" data-testid="next-q-btn">Next →</Button>
                  ) : (
                    <Button onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="submit-quiz-btn">
                      {loading ? 'Submitting...' : 'Submit Test'}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
            {/* Question grid jumper */}
            <div className="p-4 rounded-2xl bg-[#142318] border border-white/10">
              <p className="text-xs text-white/50 mb-3 uppercase tracking-wider">Jump to question</p>
              <div className="grid grid-cols-10 sm:grid-cols-15 gap-1.5">
                {quiz.questions.map((_, i) => (
                  <button key={i} onClick={() => setCurrentQ(i)}
                    className={`aspect-square rounded text-xs font-bold ${
                      i === currentQ ? 'bg-[#c8a84e] text-[#0c1a0e]' :
                      answers[i] !== null ? 'bg-emerald-500/30 text-emerald-300' :
                      'bg-white/5 text-white/40 hover:bg-white/10'
                    }`}>{i + 1}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {stage === 'result' && result && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-6" data-testid="quiz-result">
            <div className="p-8 rounded-2xl bg-gradient-to-br from-[#142318] to-[#0c1a0e] border border-[#c8a84e]/30 text-center">
              <Trophy className="w-12 h-12 text-[#c8a84e] mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Test Submitted!</h2>
              <p className="text-white/60 text-sm mb-6">Great job, {guest.name}. Here's your result:</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="p-4 rounded-xl bg-[#0c1a0e] border border-emerald-500/20"><p className="text-2xl font-bold text-emerald-400">{result.correct}</p><p className="text-xs text-white/50 mt-1">Correct</p></div>
                <div className="p-4 rounded-xl bg-[#0c1a0e] border border-red-500/20"><p className="text-2xl font-bold text-red-400">{result.wrong}</p><p className="text-xs text-white/50 mt-1">Wrong</p></div>
                <div className="p-4 rounded-xl bg-[#0c1a0e] border border-white/10"><p className="text-2xl font-bold text-white/70">{result.skipped}</p><p className="text-xs text-white/50 mt-1">Skipped</p></div>
                <div className="p-4 rounded-xl bg-[#0c1a0e] border border-[#c8a84e]/30"><p className="text-2xl font-bold text-[#c8a84e]">{result.percentage}%</p><p className="text-xs text-white/50 mt-1">Score</p></div>
              </div>
              <div className="p-4 rounded-xl bg-[#c8a84e]/10 border border-[#c8a84e]/20">
                <p className="text-3xl font-bold text-[#c8a84e] mb-1">{result.score} / {result.total}</p>
                <p className="text-xs text-white/60">Final Marks (with -1/3 negative)</p>
              </div>
              <a href={`https://wa.me/${WA_NUMBER}?text=Hi, I scored ${result.score}/${result.total} (${result.percentage}%) in your daily mock test. Tell me about your courses.`} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 mt-6 bg-[#25D366] text-white px-6 py-3 rounded-full font-semibold" data-testid="result-whatsapp-btn">
                Share Result on WhatsApp →
              </a>
            </div>
            {/* Per-question review */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Review Answers</h3>
              {result.feedback.map((f, i) => {
                const youCorrect = f.your_answer === f.correct_index;
                const skipped = f.your_answer === null || f.your_answer === -1;
                return (
                  <div key={i} className="p-4 rounded-xl bg-[#142318] border border-white/10">
                    <div className="flex items-start gap-2 mb-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${skipped ? 'bg-white/10 text-white/50' : youCorrect ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                        {skipped ? '–' : youCorrect ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                      </span>
                      <p className="text-sm text-white font-semibold flex-1 whitespace-pre-wrap">{i + 1}. {f.question}</p>
                    </div>
                    <div className="space-y-1.5 mb-2">
                      {f.options.map((opt, j) => {
                        const isCorrect = j === f.correct_index;
                        const isYour = j === f.your_answer;
                        return (
                          <div key={j} className={`text-xs px-3 py-2 rounded ${isCorrect ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : isYour ? 'bg-red-500/15 text-red-300 border border-red-500/30' : 'text-white/50'}`}>
                            <span className="font-bold">{String.fromCharCode(65 + j)}.</span> {opt}
                            {isCorrect && <span className="ml-2 text-[10px] uppercase tracking-wider">✓ Correct</span>}
                            {isYour && !isCorrect && <span className="ml-2 text-[10px] uppercase tracking-wider">Your answer</span>}
                          </div>
                        );
                      })}
                    </div>
                    {f.explanation && <p className="text-xs text-white/60 mt-2 border-l-2 border-[#c8a84e]/40 pl-3"><span className="text-[#c8a84e] font-semibold">Explanation: </span>{f.explanation}</p>}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2 justify-center pt-4">
              <Link to="/"><Button variant="outline" className="border-white/15 text-white bg-transparent hover:bg-white/5">Back to Home</Button></Link>
              <a href={`https://wa.me/${WA_NUMBER}?text=I want to join Signature PSC courses after taking the mock test.`} target="_blank" rel="noopener noreferrer">
                <Button className="bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white">Enquire about Courses →</Button>
              </a>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
